import React, { useEffect, useState, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import ApiService from '../../Components/apiService.js';
import { Spin } from 'antd';
import { Avatar, Checkbox, Tag, Image } from 'antd';
import { PhoneOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { BASE_URL, DateFormat } from '../../Components/Constant';
import { getUniqueColor } from '../../Components/Utility';
import { useSelector } from 'react-redux';
import { LookupCell } from '../../Record/LookupCell';

const TabularReportViewer = ({ reportId, getHighlightRowStyle, component }) => {
    const gridRef = useRef();
    const [rowData, setRowData] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [groupField, setGroupField] = useState('');
    const defaultCurrency = useSelector(state => state.data.orgItem);
    const [reportData, setReportData] = useState(null);
    const [fieldsData, setFieldsData] = useState([]);
    const [currency, setCurrency] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [fullRowData, setFullRowData] = useState([]);

    useEffect(() => {
        if (!reportId) return;
        setLoading(true);
        setError(null);

        // Fetch both report and fields in parallel
        const apiServiceForRecords = new ApiService(
            `${BASE_URL}/mt_reports/${reportId}`,
            { 'Content-Type': 'application/json' },
            'GET'
        );

        // We'll fetch fields after we get the model from the report
        apiServiceForRecords
            .makeCall()
            .then(response => {
                if (response?.report && response?.data) {
                    setReportData(response);
                    setRowData(response?.data);
                    setGroupField(response?.report?.group_fields[0]); // Default to 'Name' if no group_by specified
                    // Now fetch fields metadata
                    const apiServiceForFields = new ApiService(
                        `${BASE_URL}/mt_fields/object/${response?.report?.model}`,
                        { 'Content-Type': 'application/json' },
                        'GET'
                    );
                    return apiServiceForFields.makeCall();
                } else {
                    throw new Error("Invalid report response");
                }
            })
            .then(fieldsResponse => {
                setFieldsData(fieldsResponse);
            })
            .catch(err => {
                setError(err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [reportId]);

    useEffect(() => {
        if (reportData && fieldsData.length > 0) {
            createColumnDefs();

           if (groupField) {
                    const { finalData, allGroupKeys } = groupData(reportData?.data, groupField);
                    setFullRowData(finalData);

                    if (reportData?.report?.is_collapsed) {
                        // collapse all groups
                        setExpandedGroups(new Set());
                    } else {
                        // expand all groups
                        setExpandedGroups(new Set(allGroupKeys));
                    }
                }
                else {
                setRowData(reportData?.data);
                setExpandedGroups(new Set());
            }
        }
    }, [reportData, fieldsData, groupField]);

    useEffect(() => {
        if (groupField) {
            const filteredData = fullRowData.filter(row => {
                if (row.isGroup) {
                    return true; // Always show group headers
                }
                if (row._parentGroup) {
                    return expandedGroups.has(row._parentGroup); // Show if parent is expanded
                }
                return true;
            });
            setRowData(filteredData);
        }
    }, [expandedGroups, fullRowData, groupField]);

    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            gridRef.current.api.resetRowHeights();
        }
    }, [expandedGroups]);

    useEffect(() => {
        setCurrency(defaultCurrency?.currency);
    }, [defaultCurrency])

    const toggleGroup = (groupKey) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupKey)) {
                next.delete(groupKey);
            } else {
                next.add(groupKey);
            }
            return next;
        });
    };

    const groupData = (data, groupByField) => {
        const grouped = {};

        data.forEach(record => {
            let key = record[groupByField];
            let displayName = key;

            // If it's a lookup object, extract ID & Name
            if (typeof key === 'object' && key !== null) {
                displayName = key?.Name || '—';
                key = key?._id || displayName; // use _id for unique grouping!
            }

            if (!grouped[key]) grouped[key] = { displayName, records: [] };
            grouped[key].records.push(record);
        });

        const finalData = [];
        const allGroupKeys = [];

        Object.entries(grouped).forEach(([groupKey, { displayName, records }]) => {
            finalData.push({
                isGroup: true,
                groupKey,         // unique ID
                groupValue: displayName, // user-friendly label
                groupCount: records.length,
                _children: records
            });

            finalData.push(...records.map(r => ({ ...r, _parentGroup: groupKey })));

            allGroupKeys.push(groupKey);
        });

        return { finalData, allGroupKeys };
    };

    const createColumnDefs = () => {
        const fieldNames = reportData?.report?.fields_to_display || [];
        const fields = fieldNames
            .map(fieldName => {
                const found = fieldsData.find(f => f.name === fieldName);
                if (found) return found;
                if (fieldName === "created_at" || fieldName === "updated_at") {
                    return {
                        name: fieldName,
                        label: fieldName === "created_at" ? "Created At" : "Updated At",
                        type: "datetime",
                    };
                }
                return null;
            })
            .filter(Boolean);

        const columns = fields.map((field, index) => ({
            headerTooltip: field.label,
            headerName: field.label,
            hide: field.name === groupField ? groupField : false,
            field: field.name,
            key: field.name,
            type: field.type,
            flex: field.name === 'Name' ? 0 : (field.type === 'address' || field.type === 'multiselect' || field.type === 'image') ? 2 : 1,
            minWidth: field.name === 'Name' ? 90 : 90,
            maxWidth: field.name === 'Name' ? 140 : undefined,
            pinned: field.name === 'Name' ? 'left' : undefined,
            suppressMovable: field.name === 'Name',
            tooltipValueGetter: (params) => {
                const value = params.data?.[field.name];
                // Suppress tooltip for objects or arrays
                if (typeof value === "object") {
                    return null; // no tooltip
                }
                return value;
            },

            valueGetter: field.type === 'date'
                ? (params) => {
                    const val = params.data[field.name];
                    return val ? new Date(val) : null;
                }
                :
                undefined,
            autoHeight: true,
            cellStyle: (field?.type === 'currency' || field?.type === 'rollup')
                ? { justifyContent: 'right', whiteSpace: 'normal', wordWrap: 'break-word', paddingRight: '30px' }
                : field?.type === 'lookup'
                    ? { justifyContent: 'flex-start', textAlign: 'left', whiteSpace: 'normal', wordWrap: 'break-word' }
                    : { whiteSpace: 'normal', wordWrap: 'break-word' },
            headerTooltip: field?.formula || field?.is_formula ? field?.formula : '',
            headerClass: 'custom-header-cell',
            filterValueGetter: field.type === 'lookup'
                ? (params) => params.data?.[field.name]?.Name || ''
                : undefined,
            comparator: field.type === 'lookup'
                ? (valueA, valueB, nodeA, nodeB) => {
                    // valueA and valueB are the lookup objects (or undefined)
                    const nameA = valueA?.Name?.toLowerCase?.() || '';
                    const nameB = valueB?.Name?.toLowerCase?.() || '';
                    if (nameA < nameB) return -1;
                    if (nameA > nameB) return 1;
                    return 0;
                }
                : undefined,
            cellRenderer: (params) => {

                if (field.name === 'Name' && index === 0) {

                    const redirectUrl = `/record/${reportData?.report?.model}/${params?.data?._id}`;
                    return (
                        <div
                            style={{ position: "relative", display: "flex", alignItems: "center" }}
                        >
                            <a href={redirectUrl} target="_blank" rel="noopener noreferrer">{params?.data[field?.name]}</a>
                        </div>
                    );
                }
                else {
                    if (field.type === 'date') {
                        return params?.data?.[field?.name] ? dayjs(params?.data?.[field?.name]).format(DateFormat) : '';
                    }
                    else if (field.type === 'string') {
                        return params?.data?.[field?.name] || '';
                    }
                    else if (field.type === 'image') {
                        return params?.data?.[field?.name] ? (
                            <Image
                                src={params?.data?.[field?.name]}
                                width={80}
                                style={{ height: 80 || 'auto', borderRadius: '8px' }}
                                alt="image"
                            />
                        ) : (
                            ''
                        )
                    }
                    else if (field.type === 'datetime') {
                        const dateInput = params?.data?.[field.name];
                        return dateInput && dayjs(dateInput).isValid()
                            ? dayjs(dateInput).format('DD/MM/YYYY hh:mm A')
                            : '';
                    }
                    else if (field.type === 'currency') {
                        const value = params?.data?.[field?.name];
                        return value === 0 ? `${currency || '$'}0.00` : value ? `${currency || '$'}${Number(value).toFixed(2)}` : ''; // Format as currency with dollar sign
                    }
                    else if (field.type === 'rollup') {
                        return params?.data?.[field?.name] === 0 ? `0.00` : params?.data?.[field?.name] ? `${params?.data?.[field?.name]?.toFixed(2)}` : ''; // Format as currency with dollar sign
                    }
                    else if (field.type === 'percentage') {
                        return params?.data?.[field?.name] ? `${params?.data?.[field?.name]}%` : ''; // Format as currency with dollar sign
                    }
                    else if (field.type === 'decimal') {
                        const value = params?.data?.[field.name];
                        return value === undefined || value === null || value === '' || isNaN(value)
                            ? ''
                            : Number(value).toFixed(2);
                    } else if (field.type === 'email') {
                        return params?.data?.[field?.name] ? (
                            <a href={`mailto:${params?.data?.[field?.name]}`} target="_blank" rel="noopener noreferrer">
                                {params?.data?.[field?.name]}
                            </a>
                        ) : '';
                    } else if (field.type === 'url') {
                        return params?.data?.[field?.name] ? (
                            <a href={params?.data?.[field?.name].startsWith('http') ? params?.data?.[field?.name] : `http://${params?.data?.[field?.name]}`} target="_blank" rel="noopener noreferrer">
                                {params?.data?.[field?.name]}
                            </a>
                        ) : '';
                    } else if (field?.type === 'lookup') {
                        let lookupId = params?.data?.[field?.name]; // Safely access the field
                        const ob = field?.parent_object_name;

                        if (!lookupId?._id) {
                            return '';
                        } else {
                            if (lookupId?._id) {
                                return <LookupCell lookupId={lookupId} field={field} ob={ob} />;
                            }
                        }
                    }
                    else if (field.type === 'address') {
                        const address = params?.data[field?.name]; // Access the address object

                        if (address) {
                            // Combine address fields into a single string
                            const { street, city, state, country, postal_code } = address;
                            return [
                                street,
                                city,
                                state,
                                country,
                                postal_code,
                            ].filter(Boolean).join(', '); // Join non-empty fields with a comma 
                        }
                        return ''; // Return an empty string if the address is not available
                    }
                    else if (field.type === 'phone' && params?.data[field?.name]) {
                        // Add PhoneOutlined for phone numbers

                        return (
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <PhoneOutlined style={{ marginRight: 8 }} />

                                {params?.data?.[field?.name] || ''}
                            </div>
                        );
                    }
                    else if (field.type === 'richtext') {
                        return (
                            <div dangerouslySetInnerHTML={{ __html: params?.data?.[field?.name] }} />
                        );
                    }
                    else if (field.type === 'multiselect') {
                        // Check if text is an array (i.e., the MultiSelect field has multiple values)
                        return Array.isArray(params?.data?.[field?.name]) ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {params?.data?.[field?.name].map((value, index) => (
                                    <Tag
                                        key={index}
                                        color={getUniqueColor(index)} // Get a unique color for each tag
                                        style={{
                                            fontSize: '8px', // Adjust font size for better readability
                                            padding: '6px 12px', // Adjust padding for larger size
                                            borderRadius: '8px', // Rounded corners for aesthetic appeal
                                            lineHeight: '1.5', // Adjust line height for better spacing
                                        }}
                                    >
                                        {value}
                                    </Tag>
                                ))}
                            </div>
                        ) : (
                            params?.data?.[field?.name] || ''
                        );
                    }
                    else if (field.type === 'picklist') {
                        const picklistValue = params?.data?.[field?.name];
                        if (!picklistValue) return null;

                        const picklistOptions = field?.picklist_values || [];
                        const selectedOption = picklistOptions.find(option => option.value === picklistValue);

                        return (
                            <Tag
                                color={selectedOption ? selectedOption.color : 'default'}
                                style={{
                                    fontSize: '8px',
                                    padding: '6px 12px',
                                    borderRadius: '8px',
                                    lineHeight: '1.5',
                                }}
                            >
                                {selectedOption ? selectedOption.label : picklistValue}
                            </Tag>
                        );
                    }
                    else if (field.type === 'integer') {
                        const value = params?.data?.[field.name];
                        return value === undefined || value === null || value === '' || isNaN(value)
                            ? ''
                            : Number(value);
                    }
                    else if (field.type === 'textarea') {
                        return (
                            params?.data?.[field?.name] || ''
                        );
                    }
                    else if (field.type === 'boolean') {
                        return (
                            <Checkbox
                                checked={params?.data?.[field?.name]}
                                disabled={true}
                            // onChange={(e) =>
                            //     saveEdit(params.record, field.name, e.target.checked)
                            // }
                            />
                        );
                    }
                    else if (field.type === 'formula' || field.is_formula) {
                        const value = params?.data?.[field?.name];

                        // Address object (street, city, state, country, postal_code)
                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                            // Check if it's an address-like object
                            const addressKeys = ['street', 'city', 'state', 'country', 'postal_code', 'pincode'];
                            const addressParts = addressKeys.map(k => value[k]).filter(Boolean);
                            if (addressParts.length > 0) {
                                return addressParts.join(', ');
                            }
                            // If not address, stringify object
                            return JSON.stringify(value);
                        }

                        // Array (e.g., MultiSelect)
                        if (Array.isArray(value)) {
                            return (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {value.map((v, idx) => (
                                        <Tag
                                            key={idx}
                                            color={getUniqueColor(idx)}
                                            style={{
                                                fontSize: '8px',
                                                padding: '6px 12px',
                                                borderRadius: '8px',
                                                lineHeight: '1.5',
                                            }}
                                        >
                                            {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                                        </Tag>
                                    ))}
                                </div>
                            );
                        }

                        // Boolean
                        if (typeof value === 'boolean') {
                            return <Checkbox checked={value} disabled />;
                        }

                        // Number (integer, decimal, currency, percentage)
                        if (typeof value === 'number') {
                            if (field.format === 'currency') {
                                return `${currency || '$'}${value.toFixed(2)}`;
                            }
                            if (field.format === 'percentage') {
                                return `${value}%`;
                            }
                            return value;
                        }

                        // Date or DateTime (try to parse)
                        if (typeof value === 'string' && dayjs(value).isValid()) {
                            if (field.format === 'datetime') {
                                return dayjs(value).format('DD/MM/YYYY hh:mm A');
                            }
                            if (field.format === 'date') {
                                return dayjs(value).format(DateFormat);
                            }
                        }

                        // String (Email, URL, etc.)
                        if (typeof value === 'string') {
                            // Email
                            if (value.includes('@') && value.includes('.')) {
                                return (
                                    <a href={`mailto:${value}`} target="_blank" rel="noopener noreferrer">
                                        {value}
                                    </a>
                                );
                            }
                            // URL
                            if (value.startsWith('http') || value.startsWith('www.')) {
                                return (
                                    <a href={value.startsWith('http') ? value : `http://${value}`} target="_blank" rel="noopener noreferrer">
                                        {value}
                                    </a>
                                );
                            }
                            // Default string
                            return value;
                        }

                        // Fallback: show as string
                        return value !== undefined && value !== null ? String(value) : '';
                    }
                }
            }
        }));

        // Only include group column if groupField exists
        if (groupField) {
            setColumnDefs([groupColumn, ...columns]);
        } else {
            setColumnDefs(columns);
        }
    };

    const defaultColDef = useMemo(() => ({
        filter: true,
        resizable: true,
        sortable: true,
        minWidth: 90, // More compact min width
        cellStyle: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            verticalAlign: 'middle',
            padding: '2px 4px', // Less padding for compactness
            fontSize: '11px',   // Smaller font for compact display
            whiteSpace: 'normal',
            wordBreak: 'break-word',
        }
    }), []);

    const autoGroupColumnDef = useMemo(() => ({
        headerName: 'Group',
        minWidth: 180,
        cellRendererParams: {
            suppressCount: false,
        },
    }), []);

    const groupColumn = {
        headerName: groupField || 'Group',
        field: 'groupValue',
        minWidth: 200,  // wider
        flex: 0,
        flex: 0,
        cellRenderer: (params) => {
            if (params.data?.isGroup) {
                const isExpanded = expandedGroups.has(params?.data?.groupKey);
                const icon = isExpanded ? '▼' : '▶';
                return (
                    <span
                        style={{
                            cursor: 'pointer',
                            fontWeight: 600,
                            fontSize: 11,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            flexWrap: 'wrap',        // ✅ allow text to wrap
                            whiteSpace: 'normal',    // ✅ allow multiline
                            lineHeight: '1.3',
                            wordBreak: 'break-word', // ✅ wrap long words
                        }}
                        onClick={() => toggleGroup(params?.data?.groupKey)}
                    >
                        {icon} {params?.data?.groupValue} ({params?.data?.groupCount})
                    </span>
                );
            }
            return '';
        },
        cellStyle: params => {
            if (params.data?.isGroup) {
                return {
                    backgroundColor: '#f0f0f0',
                    fontWeight: 600,
                    fontSize: 11,
                    padding: '4px 6px',
                    whiteSpace: 'normal',     // ✅ make text wrap in group cells
                    wordWrap: 'break-word',
                    lineHeight: '1.4',
                };
            }
            return {
                fontSize: 11,
                padding: '4px 6px',
                whiteSpace: 'normal',
                wordWrap: 'break-word'

            };
        }
    };

    // Combines grouping style and highlight style
    const getRowStyle = params => {
        let style = {};
        // Grouping row style
        if (groupField && params.data?.isGroup) {
            style = {
                fontWeight: 'bold',
                backgroundColor: '#f0f0f0',
                borderBottom: '1px solid #ccc'
            };
        }
        // Highlight row style (from parent)
        if (getHighlightRowStyle && !params.data?.isGroup) {
            const highlight = getHighlightRowStyle(params.data, component);
            if (highlight && highlight.style) {
                style = { ...style, ...highlight.style };
            }
        }

        // If no style, return null for AG Grid
        return Object.keys(style).length > 0 ? style : null;
    };

    const getRowHeight = params => {
        return 28; // All rows have a fixed height
    };

    if (!reportId) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'orange', fontWeight: 'bold' }}>
            Report not attached correctly.
        </div>
    );
    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Spin size="small" />
            </div>
        );
    }
    if (error) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'red', fontWeight: 'bold' }}>
            Error: {error.message || error.error}
        </div>
    );

    return (
        <>
            <div className="ag-theme-alpine custom-ag-theme"
                style={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%", // Fill parent
                    minHeight: 0,
                    flex: "1 1 auto",
                    margin: 0,
                    padding: 0,
                    borderRadius: 12,
                    boxShadow: "0 2px 16px rgba(0,0,0,0.07)",
                    background: "#f8fafc",
                    overflow: "hidden", // Prevent double scrollbars
                }}
            >
                <div style={{
                    flex: 1,
                    overflowY: "auto",   // internal vertical scroll
                    overflowX: "auto"    // horizontal scroll if needed
                }}>
                    <AgGridReact
                        ref={gridRef}
                        rowData={rowData}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        autoGroupColumnDef={autoGroupColumnDef}
                        animateRows={true}
                        rowHeight={28}
                        headerHeight={28}
                        pagination={true}
                        paginationPageSize={50}
                        domLayout="normal" // <-- Important: use "normal" for sticky footer
                        // enableBrowserTooltips={true}
                        overlayLoadingTemplate={
                            loading
                                ? '<Spin spinning={loading} size="small" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }} />'
                                : null
                        }
                        onGridReady={(params) => params.api.sizeColumnsToFit()}
                        tooltipShowDelay={50}
                        getRowStyle={getRowStyle}
                        getRowHeight={getRowHeight}
                    />
                </div>
            </div>
        </>
    );
}

export default TabularReportViewer